import { TableCell, Typography } from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { UserPublic } from 'api/model';
import useUser from 'api/users/useUser';
import { ActionIconButton } from 'components/button/ActionIconButton';
import ConfirmationDialog from 'components/dialog/ConfirmationDialog';
import useDialog from 'hooks/useDialog';
import { Pill } from 'components/indicators/Pill';
import { MetaCell, UsernameCell, UserTableRow } from './UsersView.styles';

const formatDate = (value: string | null) => (value ? new Date(value).toLocaleString() : '-');

type Props = {
  user: UserPublic;
  isSelf: boolean;
};

const UserRow = ({ user, isSelf }: Props) => {
  const { deactivate, reactivate } = useUser(user.id);
  const [showDeactivateDialog, DeactivateDialog] = useDialog(ConfirmationDialog);

  const isPending = deactivate.isPending || reactivate.isPending;

  return (
    <UserTableRow hover>
      <UsernameCell>{user.username}</UsernameCell>
      <TableCell>{user.display_name}</TableCell>
      <TableCell>
        <Pill tone={user.is_superuser ? 'primary' : 'neutral'}>{user.is_superuser ? 'Superuser' : 'User'}</Pill>
      </TableCell>
      <TableCell>
        <Pill tone={user.is_active ? 'success' : 'error'} dot>
          {user.is_active ? 'Active' : 'Inactive'}
        </Pill>
      </TableCell>
      <MetaCell>{formatDate(user.created_at)}</MetaCell>
      <MetaCell>{formatDate(user.last_login_at)}</MetaCell>
      <TableCell align="right">
        {user.is_active ? (
          <ActionIconButton
            size="small"
            color="error"
            title={isSelf ? 'You cannot deactivate your own account' : 'Deactivate'}
            disabled={isSelf || isPending}
            onClick={() => showDeactivateDialog(true)}
          >
            <BlockIcon fontSize="small" />
          </ActionIconButton>
        ) : (
          <ActionIconButton
            size="small"
            color="success"
            title="Reactivate"
            disabled={isPending}
            onClick={() => reactivate.mutate()}
          >
            <CheckCircleOutlineIcon fontSize="small" />
          </ActionIconButton>
        )}

        <DeactivateDialog
          title="Deactivate User"
          onConfirm={() => deactivate.mutate()}
          buttonsSettings={{ confirmText: 'Deactivate', confirmColor: 'error' }}
        >
          <Typography>
            Deactivate <strong>{user.username}</strong>? They will not be able to sign in and their existing sessions
            stop working.
          </Typography>
        </DeactivateDialog>
      </TableCell>
    </UserTableRow>
  );
};

export default UserRow;
